import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { useLang } from "@/lib/i18n";
import SpotlightFooterLine from "@/components/SpotlightFooterLine";
import { useSite } from "@/lib/site";
import Logo from "@/components/Logo";
import Img from "@/components/Img";

export default function Footer() {
  const { t, L } = useLang();
  const site = useSite();
  const s = site?.settings || {};
  const sectors = site?.sectors || [];
  const logoSrc =
    site?.settings?.theme === "desert-minimalist" && site?.settings?.logo_url_light
      ? site.settings.logo_url_light
      : s.logo_url;

  return (
    <footer data-testid="site-footer" className="border-t border-border bg-card/40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-12">
        <div className="sm:col-span-2 lg:col-span-5 min-w-0">
          {logoSrc ? (
            <Img
              src={logoSrc}
              width={640}
              sizes="320px"
              alt={L(s.site_name)}
              data-testid="footer-logo-img"
              className="mb-6 w-auto"
              style={{ height: `${s.logo_height_footer || 112}px` }}
            />
          ) : (
            <div className="flex items-center gap-3 text-primary mb-5">
              <Logo markSize={40} siteName={L(s.site_name)} />
              <span className="font-head text-2xl">{L(s.site_name)}</span>
            </div>
          )}
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md">{L(s.site_name_alt)}</p>
          <p className="mt-6 text-xs tracking-[0.3em] uppercase text-muted-foreground">Abu Dhabi — UAE</p>
        </div>

        <div className="lg:col-span-2 min-w-0">
          <h4 className="eyebrow mb-5">{t("footer_quick")}</h4>
          <ul className="space-y-3 text-sm">
            {["nav_about", "nav_sectors", "nav_ventures", "nav_leadership", "nav_offices", "nav_news", "nav_contact"].map((k, i) => (
              <li key={k}>
                <Link className="text-foreground/75 hover:text-primary transition-colors" data-testid={`footer-link-${i}`} to={["/about", "/sectors", "/joint-ventures", "/leadership", "/offices", "/news", "/contact"][i]}>
                  {t(k)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2 min-w-0">
          <h4 className="eyebrow mb-5">{t("footer_sectors")}</h4>
          <ul className="space-y-3 text-sm">
            {sectors.map((sec) => (
              <li key={sec.id}>
                <Link className="text-foreground/75 hover:text-primary transition-colors" data-testid={`footer-sector-${sec.id}`} to="/sectors">
                  {L(sec.name)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3 min-w-0">
          <h4 className="eyebrow mb-5">{t("footer_contact")}</h4>
          <p className="text-sm text-foreground/75 leading-relaxed">{s.address}</p>
          {s.email && <p className="mt-3 text-sm text-foreground/75" data-testid="footer-email">{s.email}</p>}
          {s.phone && <p className="mt-1 text-sm text-foreground/75" data-testid="footer-phone">{s.phone}</p>}
          {s.linkedin && (
            <a
              href={s.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-linkedin"
              className="mt-4 inline-flex items-center gap-2 text-sm text-foreground/75 transition-colors hover:text-primary"
            >
              <Linkedin className="h-4 w-4" /> LinkedIn
            </a>
          )}
        </div>
      </div>

      <SpotlightFooterLine />

      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {L(s.site_name)}. {t("rights")}</p>
          <p className="tracking-[0.25em] uppercase">{L(s.site_name_alt)}</p>
        </div>
      </div>
    </footer>
  );
}
